import { getStoredUserRole } from '@/lib/staff-roles'
/** Menu → Deals → Automatic discounts: the list (F-DEAL-N3). */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Edit, Percent, Plus, Trash2 } from 'lucide-react'
import apiClient from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { useCurrency } from '@/contexts/CurrencyContext'
import { toastHelpers } from '@/lib/toast-helpers'
import type { AutoDiscountAdmin } from '@/types/deals'
import { AutoDiscountEditorDialog } from './AutoDiscountEditorDialog'
import { AUTO_DISCOUNTS_QUERY_KEY, ConfirmDialog, useDealsCatalog, useOrderTypeOptions } from './DealsControls'
import { describeAutoDiscountRule, describeWhen, errorText } from './dealsText'
import { ruleToInput } from './autoDiscountDraft'

export function AutoDiscountsPane() {
  const { formatCurrency } = useCurrency()
  const qc = useQueryClient()
  const catalog = useDealsCatalog()
  const orderTypes = useOrderTypeOptions()
  const query = useQuery({
    queryKey: AUTO_DISCOUNTS_QUERY_KEY,
    queryFn: async () => (await apiClient.listAutoDiscounts()).data ?? ([] as AutoDiscountAdmin[]),
    staleTime: 30_000,
  })
  const rules = query.data ?? []
  const [editing, setEditing] = useState<AutoDiscountAdmin | null>(null)
  const [open, setOpen] = useState(false)
  const [removing, setRemoving] = useState<AutoDiscountAdmin | null>(null)

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: AUTO_DISCOUNTS_QUERY_KEY })
    qc.invalidateQueries({ queryKey: ['menu-rules'] })
  }

  const toggle = useMutation({
    mutationFn: (r: AutoDiscountAdmin) => apiClient.updateAutoDiscount(r.id, ruleToInput(r, { is_active: !r.is_active })),
    onSuccess: (_res, r) => {
      invalidate()
      toastHelpers.success(r.is_active ? 'Discount paused' : 'Discount turned on', r.name)
    },
    onError: (e) => toastHelpers.error('Could not change the discount', errorText(e)),
  })

  const archive = useMutation({
    mutationFn: (r: AutoDiscountAdmin) => apiClient.archiveAutoDiscount(r.id),
    onSuccess: (_res, r) => {
      invalidate()
      setRemoving(null)
      toastHelpers.success('Discount removed', r.name)
    },
    onError: (e) => toastHelpers.error('Could not remove the discount', errorText(e)),
  })

  const openEditor = (r: AutoDiscountAdmin | null) => {
    setEditing(r)
    setOpen(true)
  }

  // Owner decision 2026-11-02: only admins create or change automatic
  // discounts (like promo codes); managers see them read-only.
  const isAdmin = getStoredUserRole() === 'admin'

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl space-y-1">
          <h3 className="font-semibold">Automatic discounts</h3>
          <p className="text-sm text-muted-foreground">
            Offers that apply by themselves when a bill qualifies: buy one get one, bundle prices and spend-over rewards. Only the single best one applies to a bill.
          </p>
        </div>
        {isAdmin ? (
          <Button type="button" className="gap-2" onClick={() => openEditor(null)}>
            <Plus className="h-4 w-4" />
            New discount
          </Button>
        ) : (
          <p className="text-sm text-muted-foreground">Only an admin can create or change automatic discounts.</p>
        )}
      </div>

      {query.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : query.isError ? (
        <p className="text-sm text-destructive">Could not load automatic discounts. Refresh the page to try again.</p>
      ) : rules.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <Percent className="mx-auto mb-3 h-8 w-8 text-muted-foreground" aria-hidden />
            <p className="text-sm text-muted-foreground">
              No automatic discounts yet. Create one for offers like “Buy 1 get 1 free on Pizza” or “10% off over Rs 2,000”.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="divide-y rounded-lg border">
          {rules.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-4 p-4">
              <Switch
                checked={r.is_active}
                disabled={toggle.isPending || !isAdmin}
                aria-label={r.is_active ? `Pause ${r.name}` : `Turn on ${r.name}`}
                onCheckedChange={() => toggle.mutate(r)}
              />
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{r.name}</span>
                  {!r.is_active && <Badge variant="neutral-surface">Paused</Badge>}
                </div>
                <p className="text-sm">
                  {describeAutoDiscountRule(r, { product: catalog.productName, category: catalog.categoryName }, formatCurrency)}
                </p>
                <p className="text-xs text-muted-foreground">{describeWhen(r, orderTypes.label)}</p>
              </div>
              <div className="text-right text-sm">
                <p className="tabular-nums">
                  Used {r.times_used.toLocaleString()} time{r.times_used === 1 ? '' : 's'}
                </p>
                <p className="text-xs tabular-nums text-muted-foreground">{formatCurrency(r.discount_given)} given</p>
              </div>
              {isAdmin && <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => openEditor(r)}>
                  <Edit className="h-4 w-4" />
                  Edit
                </Button>
                <Button type="button" variant="ghost" size="sm" aria-label={`Remove ${r.name}`} onClick={() => setRemoving(r)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>}
            </div>
          ))}
        </div>
      )}

      <AutoDiscountEditorDialog open={open} onOpenChange={setOpen} rule={editing} />
      <ConfirmDialog
        open={removing != null}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={`Remove ${removing?.name ?? 'this discount'}?`}
        description="It stops applying straight away. Past bills and the Deals report keep their record."
        confirmLabel="Remove"
        busy={archive.isPending}
        onConfirm={() => removing && archive.mutate(removing)}
      />
    </div>
  )
}
